/**
 * The debris-flow numbers the modelling team's own files state, and the quantities
 * derived from them - shown wherever this platform shows a flash-flood case: the
 * Scenario page, the run record, and the Impact page.
 *
 * Every derived quantity carries the equation it used and the inputs that went into
 * it, each with the file and line it was read from
 * (GET /api/hecras/flow-model/derived, backend/app/hecras/derived.py). A quantity
 * whose inputs are not all in the files is not sent by the API and does not appear
 * here. A site with no non-Newtonian case renders nothing at all.
 */

import { useQuery } from '@tanstack/react-query'

import { api } from '../api/client'

interface Input {
  label: string
  value: number
  unit: string
  source: string
}

interface CurvePoint {
  shear_rate_s: number
  apparent_viscosity_pa_s: number
}

interface DerivedRow {
  name: string
  equation: string
  value?: number
  curve?: CurvePoint[]
  unit: string
  inputs: Input[]
  note?: string
}

interface CaseBlock {
  case_id: string
  label: string
  rows: DerivedRow[]
  sediment_transport_off: boolean
  sediment_transport_source: string | null
  sediment_off_statement: string
}

interface Payload {
  site_id: string
  run_id: string | null
  cases: CaseBlock[]
  sediment_off_statement: string
}

const UNIT: Record<string, string> = { m3: 'm³', 'm3/s': 'm³/s', Mm3: 'Mm³', 'kg/m3': 'kg/m³', 'Pa s': 'Pa·s' }

function unit(u: string): string {
  return UNIT[u] ?? u
}

/** Three significant figures for a derived number, and thousands separated. */
function show(value: number): string {
  if (!Number.isFinite(value)) return ''
  const abs = Math.abs(value)
  if (abs >= 1000) return value.toLocaleString('en-IN', { maximumFractionDigits: 0 })
  if (abs >= 10) return value.toFixed(1)
  if (abs >= 1) return value.toFixed(2)
  return value.toFixed(3)
}

function Row({ row }: { row: DerivedRow }) {
  return (
    <div className="border-t border-[var(--ink-border)] py-3 first:border-t-0" data-testid={`derived-${row.name.replace(/\W+/g, '-').toLowerCase()}`}>
      <div className="flex flex-wrap items-baseline justify-between gap-x-4 gap-y-1">
        <span className="text-14 font-medium text-[var(--ink-text)]">{row.name}</span>
        {row.value !== undefined && (
          <span className="font-mono text-16 tabular-nums text-[var(--ink-text)]">
            {show(row.value)} {unit(row.unit)}
          </span>
        )}
      </div>
      <div className="mt-1 font-mono text-12 text-[var(--ink-accent)]">{row.equation}</div>
      {row.curve && (
        <table className="mt-2 text-12 tabular-nums">
          <thead>
            <tr>
              <th className="pr-4 text-left font-medium text-[var(--ink-text-dim)]">Shear rate (1/s)</th>
              {row.curve.map((c) => (
                <th key={c.shear_rate_s} className="px-3 text-right font-mono font-normal text-[var(--ink-text-dim)]">
                  {c.shear_rate_s}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            <tr>
              <td className="pr-4 text-[var(--ink-text-dim)]">Apparent viscosity ({unit(row.unit)})</td>
              {row.curve.map((c) => (
                <td key={c.shear_rate_s} className="px-3 text-right font-mono text-[var(--ink-text)]">
                  {show(c.apparent_viscosity_pa_s)}
                </td>
              ))}
            </tr>
          </tbody>
        </table>
      )}
      <div className="mt-1.5 flex flex-wrap gap-x-4 gap-y-0.5 text-12 text-[var(--ink-text-faint)]">
        {row.inputs.map((i) => (
          <span key={i.label + i.source}>
            {i.label} = <span className="font-mono tabular-nums">{show(i.value)}</span>
            {i.unit ? ` ${unit(i.unit)}` : ''} <span className="font-mono">({i.source})</span>
          </span>
        ))}
      </div>
      {row.note && <p className="mt-1 max-w-[70ch] text-12 leading-relaxed text-[var(--ink-text-dim)]">{row.note}</p>}
    </div>
  )
}

export function DebrisQuantities({
  siteId = null,
  runId,
  block,
  title = 'Debris flow: what their files state, and what follows from it',
  bare = false,
}: {
  /** Fetch every case at this site. Ignored when `block` is given. */
  siteId?: string | null
  runId?: string | null
  /** Show one case already fetched by the page (the run record, the Impact exposure payload). */
  block?: CaseBlock | null
  title?: string
  /** Inside a card that has its own heading: no panel, no title. */
  bare?: boolean
}) {
  const query = useQuery<Payload>({
    queryKey: ['debris-derived', siteId, runId ?? null],
    queryFn: () =>
      api.raw(`/hecras/flow-model/derived?site_id=${encodeURIComponent(siteId!)}${runId ? `&run_id=${encodeURIComponent(runId)}` : ''}`),
    enabled: Boolean(siteId) && !block,
    staleTime: Infinity,
  })
  const cases = block ? [block] : (query.data?.cases ?? [])
  if (cases.length === 0) return null
  const off = cases.find((c) => c.sediment_transport_off)
  const Wrapper = bare ? 'div' : 'section'
  return (
    <Wrapper className={bare ? '' : 'panel p-4'} data-testid="debris-quantities">
      {!bare && <h3 className="panel-title mb-1">{title}</h3>}
      {off && (
        <p className="mb-3 max-w-[80ch] text-12 leading-relaxed text-[var(--ink-text-dim)]" data-testid="sediment-off">
          {off.sediment_off_statement} <span className="font-mono">({off.sediment_transport_source})</span>
        </p>
      )}
      {cases.map((c) => (
        <div key={c.case_id} className="mt-3 first:mt-0">
          {cases.length > 1 && <div className="section-label mb-1">{c.label}</div>}
          {c.rows.map((row) => (
            <Row key={row.name} row={row} />
          ))}
        </div>
      ))}
    </Wrapper>
  )
}

export type { CaseBlock as DebrisCase }
